/**
 * Shared tone palette for status / RAG / role / priority / outcome chips
 * and progress bars. Promotes a half-dozen inconsistent local lookup tables
 * (ProjectDetailPage, TenantBillingPage, TenantOrganisationPage,
 * TranscriptsPage, ReportsPage) into one canonical source so chips, pills,
 * and bar fills all reach for the same palette.
 *
 * Each helper returns the same `Tone` shape so callers can pick which face
 * to render (.chip for outline badges, .pill for tinted backgrounds, .text
 * for inline annotations, .bar for progress-bar fills). Unknown values
 * resolve to a neutral muted tone so the helpers never throw at runtime.
 */

export interface Tone {
  /** Classes for an outline/ghost badge — border + text, no background fill. */
  chip: string;
  /** Classes for a saturated-tint badge — bg + text. */
  pill: string;
  /** Just the text colour, for inline annotations. */
  text: string;
  /** Just the background colour, for progress-bar fills. */
  bar: string;
}

const TONES = {
  emerald: {
    chip: 'border-emerald-500 text-emerald-600 dark:text-emerald-400',
    pill: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    text: 'text-emerald-600 dark:text-emerald-400',
    bar: 'bg-emerald-500',
  },
  amber: {
    chip: 'border-amber-500 text-amber-600 dark:text-amber-400',
    pill: 'bg-amber-500/15 text-amber-800 dark:text-amber-300',
    text: 'text-amber-600 dark:text-amber-400',
    bar: 'bg-amber-500',
  },
  rose: {
    chip: 'border-rose-500 text-rose-600 dark:text-rose-400',
    pill: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
    text: 'text-rose-600 dark:text-rose-400',
    bar: 'bg-rose-500',
  },
  sky: {
    chip: 'border-sky-500 text-sky-600 dark:text-sky-400',
    pill: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
    text: 'text-sky-600 dark:text-sky-400',
    bar: 'bg-sky-500',
  },
  violet: {
    chip: 'border-violet-500 text-violet-600 dark:text-violet-400',
    pill: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
    text: 'text-violet-600 dark:text-violet-400',
    bar: 'bg-violet-500',
  },
  primary: {
    chip: 'border-primary text-primary',
    pill: 'bg-primary/15 text-primary',
    text: 'text-primary',
    bar: 'bg-primary',
  },
  neutral: {
    chip: 'border-border text-muted-foreground',
    pill: 'bg-muted text-muted-foreground',
    text: 'text-muted-foreground',
    bar: 'bg-muted-foreground',
  },
} as const satisfies Record<string, Tone>;

// -- Status (subscription / tenant billing) -----------------------------------

export function statusTone(status: string): Tone {
  switch (status) {
    case 'Active':
      return TONES.emerald;
    case 'Trialing':
    case 'Trial':
      return TONES.sky;
    case 'PastDue':
    case 'Suspended':
    case 'Paused':
      return TONES.amber;
    case 'Cancelled':
      return TONES.rose;
    default:
      return TONES.neutral;
  }
}

// -- RAG (Red / Amber / Green) ------------------------------------------------

export function ragTone(rag: string): Tone {
  switch (rag) {
    case 'Green':
      return TONES.emerald;
    case 'Amber':
      return TONES.amber;
    case 'Red':
      return TONES.rose;
    default:
      return TONES.neutral;
  }
}

// -- Role ---------------------------------------------------------------------

export function roleTone(role: string): Tone {
  switch (role) {
    case 'Admin':
    case 'TenantAdmin':
      return TONES.rose;
    case 'Executive':
      return TONES.primary;
    case 'ExecutiveAssistant':
      return TONES.violet;
    default:
      return TONES.neutral;
  }
}

// -- Priority -----------------------------------------------------------------

export function priorityTone(priority: string): Tone {
  switch (priority) {
    case 'Critical':
      return TONES.rose;
    case 'High':
      return TONES.amber;
    case 'Medium':
      return TONES.sky;
    case 'Low':
      return TONES.neutral;
    default:
      return TONES.neutral;
  }
}

// -- Outcome ------------------------------------------------------------------

export function outcomeTone(status: string): Tone {
  switch (status) {
    case 'Achieved':
      return TONES.emerald;
    case 'InProgress':
      return TONES.sky;
    case 'Missed':
      return TONES.rose;
    case 'Deferred':
      return TONES.amber;
    case 'NotStarted':
    default:
      return TONES.neutral;
  }
}
